import { useQuery } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';

import { buildRpmOverviewDashboardFilter } from './utils';

import type {
  TDataFilterWithPaginationMeta,
  TDataFilter,
} from './use-rpm-overview-dashboard';
import type { ReadingStatus } from 'infrastructure/enums';

export interface TFilterCountWithoutPagination {
  latestReadingStartTime?: number;
  latestReadingEndTime?: number;
  year?: number;
  month?: number;
  reviewed?: boolean;
  readingStatus?: ReadingStatus;
}

export interface FiltersCount {
  all: number;
  assignedToMe: number;
  freeToAssign: number;
}

const url = 'oneplatform/patients/count-by-assignment';

export const getAssigneeFiltersCountQueryKey = (filter: TDataFilter) => [
  url,
  filter,
];

const queryFn = async (filter: TDataFilter) => {
  const { data, error } = await dataService.getList<FiltersCount>(
    apiUrlGenerator(url, filter),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useAssigneeFiltersCount = (
  filter: TDataFilterWithPaginationMeta,
) => {
  const filterWithoutPagination = buildRpmOverviewDashboardFilter(filter);

  const queryKey = getAssigneeFiltersCountQueryKey(filterWithoutPagination);

  const { data, isLoading, isFetching, error } = useQuery<
    FiltersCount | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn(filterWithoutPagination),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return {
    filtersCount: data,
    isLoading,
    isFetching,
  };
};
